import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Accordion,
  Table
} from 'react-bootstrap'
import Search from 'shared/forms/search'
import { usePermissions } from 'api/hooks/use-permissions'
import useAnalyticPermissions from 'api/hooks/use-analytic-permissions'
import { toKebabCase } from 'helpers/utils'
import {
  faDownload,
  faExclamationTriangle,
  faExclamationCircle,
  faPowerOff,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

function groupByCategory(items) {
  return items.reduce((acc, item) => {
    const key = item.category_caption || 'Uncategorized'
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})
}

function BuildRole({ show, setShow }) {
  const [roleName, setRoleName] = useState('')
  const [params, setParams] = useState({
    search: '',
    orderBy: 'name',
    orderDirection: 'asc',
    perPage: 1000,
    page: 1
  })
  const [selected, setSelected] = useState({})
  const [selectedValues, setSelectedValues] = useState({})
  const [conflicts, setConflicts] = useState([])
  const [activeKey, setActiveKey] = useState('')

  const handleClose = () => setShow(false)

  const { data, isSuccess } = usePermissions({ params, options: { enabled: show } })
  const { data: analyticPermissions, isSuccess: analyticSuccess } =
    useAnalyticPermissions({ options: { enabled: show } })

  const togglePermission = (perm) => {
    setSelected((prev) => {
      const cat = perm.category_caption || 'Uncategorized'
      const perms = prev[cat] || []
      const exists = perms.find((p) => p.id === perm.id)
      if (exists) {
        const updated = perms.filter((p) => p.id !== perm.id)
        const obj = { ...prev }
        if (updated.length) {
          obj[cat] = updated
        } else {
          delete obj[cat]
        }
        return obj
      } else {
        return { ...prev, [cat]: [...perms, perm] }
      }
    })

    setSelectedValues((prev) => {
      if (prev[perm.id]) {
        const copy = { ...prev }
        delete copy[perm.id]
        return copy
      } else {
        const expansiveValue = perm.permission_values?.find(
          (pv) => pv.is_expansive
        )
        return {
          ...prev,
          [perm.id]: expansiveValue ? expansiveValue.id : ''
        }
      }
    })
  }

  const groupedPermissions = isSuccess ? groupByCategory(data.data) : {}
  const groupedSelected = groupByCategory(Object.values(selected).flat())

  const highRiskCount = Object.values(selected)
    .flat()
    .filter((p) => p.is_high_risk).length

  const superUserCount = Object.values(selected)
    .flat()
    .filter((p) => p.is_superuser).length

  const internalConflictCount = conflicts.length

  useEffect(() => {
    if (!analyticSuccess) return

    const selectedIds = Object.values(selected)
      .flat()
      .map((p) => p.id)

    const conflictsFound = analyticPermissions.filter((ap) => {
      const permIds = ap.permission_variable.permissions.map((p) => p.id)
      const conflictIds = ap.conflicting_variable.permissions.map((p) => p.id)
      return (
        permIds.some((id) => selectedIds.includes(id)) &&
        conflictIds.some((id) => selectedIds.includes(id))
      )
    })

    setConflicts(conflictsFound)
  }, [selected, analyticSuccess, analyticPermissions])

  const handleDownload = () => {
    const header =
      'role_name,permission_category,permission_name,permission_value\n'
    const csvRows = Object.values(selected)
      .flat()
      .map((perm) => {
        const selectedValueId = selectedValues[perm.id]
        const pv = perm.permission_values?.find((v) => v.id === selectedValueId)
        return [
          roleName,
          perm.category_caption || 'Uncategorized',
          perm.description,
          pv ? pv.value : ''
        ]
          .map((field) => `"${String(field).replace(/"/g, '""')}"`)
          .join(',')
      })
      .join('\n')
    const csvContent = header + csvRows
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `${toKebabCase(roleName || 'role')}-conflicts.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Build Role</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3 border-bottom pb-3">
          <Col sm={3}>
            <Form.Label>Role Name</Form.Label>
          </Col>
          <Col sm={9}>
            <Form.Control
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Enter role name"
            />
          </Col>
        </Row>
        <Row>
          <Col
            sm={6}
            className="border-end"
            style={{ maxHeight: '60vh', overflowY: 'auto' }}
          >
            <div className="sticky-top bg-white p-2">
              <Search
                params={params}
                setParams={setParams}
                placeholder="Search Permissions"
              />
            </div>
            <div className="mt-3">
              {isSuccess &&
                Object.keys(groupedPermissions).map((cat) => (
                  <div key={cat} className="mb-3">
                    <div className="fw-bold">{cat}</div>
                    <ul className="list-unstyled ms-3">
                      {groupedPermissions[cat].map((perm) => (
                        <li key={perm.id}>
                          <Form.Check
                            type="checkbox"
                            id={`perm-${perm.id}`}
                            label={perm.description}
                            checked={
                              !!selected[cat]?.find((p) => p.id === perm.id)
                            }
                            onChange={() => togglePermission(perm)}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </Col>
          <Col sm={6} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="fw-bold mb-2">Selected Permissions</div>
            {Object.keys(groupedSelected).length === 0 && (
              <div className="text-muted">No permissions selected</div>
            )}
            {Object.entries(groupedSelected).map(([cat, perms]) => (
              <div key={`selected-${cat}`} className="mb-3">
                <div className="fw-bold">{cat}</div>
                <ul className="list-unstyled ms-3">
                  {perms.map((perm) => (
                    <li key={`sel-${perm.id}`} className="mb-2">
                      <Row className="d-flex align-items-center">
                        <Col sm={8}>
                          <span className="me-2">{perm.description}</span>
                        </Col>
                        <Col sm={4} className="text-end">
                          <div className="d-flex justify-content-end align-items-center">
                            <Form.Select
                              size="sm"
                              value={selectedValues[perm.id] || ''}
                              onChange={(e) =>
                                setSelectedValues({
                                  ...selectedValues,
                                  [perm.id]: e.target.value
                                })
                              }
                              className="me-2"
                            >
                              <option value="">Select Value</option>
                              {perm.permission_values.map((pv) => (
                                <option key={pv.id} value={pv.id}>
                                  {pv.value}
                                  {!!pv.value_caption
                                    ? ' - ' + pv.value_caption
                                    : ''}
                                </option>
                              ))}
                            </Form.Select>
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="me-2 text-danger text-small"
                              style={{ cursor: 'pointer' }}
                              onClick={() => togglePermission(perm)}
                            />
                          </div>
                        </Col>
                      </Row>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Col>
        </Row>

        <hr className="my-4" />

        <Row className="my-3">
          <Col sm="auto">
            <FontAwesomeIcon
              icon={faExclamationCircle}
              className="text-warning"
            />
            <span className="text-muted ms-1">{highRiskCount}</span>
            <span className="text-muted ms-1">High Risk Permissions</span>
          </Col>
          <Col sm="auto">
            <FontAwesomeIcon icon={faPowerOff} className="text-danger" />
            <span className="text-muted ms-1">{superUserCount}</span>
            <span className="text-muted ms-1">Super User Permissions</span>
          </Col>
          <Col sm="auto">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-danger"
            />
            <span className="text-muted ms-1">{internalConflictCount}</span>
            <span className="text-muted ms-1">Internal Conflicts</span>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col>
            <div className="fw-bold mb-2">Conflicts</div>
            {conflicts.length === 0 && (
              <div className="text-muted">No conflicts detected</div>
            )}
            {conflicts.length > 0 && (
              <Accordion activeKey={activeKey} flush>
                {conflicts.map((conflict) => {
                  const selectedIds = Object.values(selected)
                    .flat()
                    .map((p) => p.id)
                  const perms = conflict.permission_variable.permissions.filter(
                    (p) => selectedIds.includes(p.id)
                  )
                  const conflictsList =
                    conflict.conflicting_variable.permissions.filter((p) =>
                      selectedIds.includes(p.id)
                    )
                  return (
                    <Accordion.Item
                      eventKey={`${conflict.id}`}
                      key={`conflict-${conflict.id}`}
                    >
                      <Accordion.Header
                        onClick={() =>
                          setActiveKey(
                            activeKey === conflict.id ? '' : `${conflict.id}`
                          )
                        }
                      >
                        <h6>
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="text-danger me-2"
                          />
                          {conflict.analytic.name}
                        </h6>
                      </Accordion.Header>
                      <Accordion.Body>
                        <Table bordered size="sm" className="ms-3">
                          <thead className="table-light">
                            <tr>
                              <th>{conflict.permission_variable.name}</th>
                              <th>{conflict.conflicting_variable.name}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <ul className="mb-0 ps-3">
                                  {perms.map((p) => (
                                    <li
                                      key={p.id}
                                      className="d-flex justify-content-between"
                                    >
                                      <span>{p.description}</span>
                                    </li>
                                  ))}
                                </ul>
                              </td>
                              <td>
                                <ul className="mb-0 ps-3">
                                  {conflictsList.map((p) => (
                                    <li
                                      key={p.id}
                                      className="d-flex justify-content-between"
                                    >
                                      <span>{p.description}</span>
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  )
                })}
              </Accordion>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleDownload}>
          <FontAwesomeIcon icon={faDownload} className="me-2" />
          Download Role
        </Button>
        <Button variant="light" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default BuildRole
